// State of the /loans/closed filter bar (D52): the period as UTC dates (inclusive), the location and the coin. It
// lives in the URL only (deep-linkable); a preset is just a pair of dates, so a bookmarked "ez a hónap" keeps meaning
// the month it was made in. Everything is cut on UTC boundaries (fleet frontend standard 6e). Pure functions.
import type { ClosedQuery } from '../api/model';

export type ClosedPreset = 'month' | 'prev' | 'year' | 'all';
export const PRESETS: readonly ClosedPreset[] = ['month', 'prev', 'year', 'all'];
export const PRESET_LABEL: Record<ClosedPreset, string> = { month: 'Ez a hónap', prev: 'Előző hónap', year: 'Idei év', all: 'Mind' };
export const PAGE_SIZE = 200;

export interface ClosedFilter {
  /** UTC dates "YYYY-MM-DD", inclusive; '' = open ended. */
  from: string;
  to: string;
  locationId: string;
  asset: string;
  /** 1-based. */
  page: number;
}

export const DEFAULT_CLOSED_FILTER: ClosedFilter = { from: '', to: '', locationId: '', asset: '', page: 1 };

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n: number): string => String(n).padStart(2, '0');
const ymd = (d: Date): string => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/** True for a real calendar date ("2026-02-31" is not one). */
export function isUtcDate(v: string): boolean {
  if (!DATE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && ymd(d) === v;
}

/** The date range of a preset, cut on the UTC calendar ("Ez a hónap" = the whole current UTC month). */
export function presetRange(preset: ClosedPreset, now: Date): { from: string; to: string } {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (preset) {
    case 'month':
      return { from: ymd(new Date(Date.UTC(y, m, 1))), to: ymd(new Date(Date.UTC(y, m + 1, 0))) };
    case 'prev':
      return { from: ymd(new Date(Date.UTC(y, m - 1, 1))), to: ymd(new Date(Date.UTC(y, m, 0))) };
    case 'year':
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'all':
      return { from: '', to: '' };
  }
}

/** Which preset the current dates are, or null for a custom range. */
export function activePreset(f: Pick<ClosedFilter, 'from' | 'to'>, now: Date): ClosedPreset | null {
  return PRESETS.find((p) => {
    const r = presetRange(p, now);
    return r.from === f.from && r.to === f.to;
  }) ?? null;
}

export function closedFilterToParams(f: ClosedFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.locationId) p.set('loc', f.locationId);
  if (f.asset) p.set('asset', f.asset);
  if (f.page > 1) p.set('page', String(f.page));
  return p;
}

/** Bad values fall back to the default (an invalid date is dropped, never sent to the API). */
export function paramsToClosedFilter(p: URLSearchParams): ClosedFilter {
  const from = p.get('from') ?? '';
  const to = p.get('to') ?? '';
  const page = Number(p.get('page'));
  const asset = (p.get('asset') ?? '').trim().toUpperCase();
  return {
    from: isUtcDate(from) ? from : '',
    to: isUtcDate(to) ? to : '',
    locationId: p.get('loc') ?? '',
    asset: /^[A-Z0-9]{1,30}$/.test(asset) ? asset : '',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

export interface BuiltClosedQuery {
  query: ClosedQuery;
  /** Hungarian message when the period cannot be used (the page then skips the request). */
  periodError: string | null;
}

export function closedFilterToQuery(f: ClosedFilter): BuiltClosedQuery {
  const q: ClosedQuery = { limit: PAGE_SIZE, offset: (Math.max(f.page, 1) - 1) * PAGE_SIZE };
  if (f.from) q.date_from = f.from;
  if (f.to) q.date_to = f.to;
  if (f.locationId) q.location_id = f.locationId;
  if (f.asset) q.asset = f.asset;
  const periodError = f.from && f.to && f.from > f.to ? 'Az időszak eleje nem lehet a vége után.' : null;
  return { query: q, periodError };
}
